/* eslint-disable no-shadow */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AcceptancePlayerCard from './AcceptancePlayerCard';
import {
    acceptanceBucketTypeIds,
    acceptanceBucketTypeSortKey,
    AllowedBucketTypesForMovingToAlternate,
    AllowedBucketTypesForMovingToMain,
    AllowedBucketTypesForMovingToQualifying,
    AllowedBucketTypesForTagging,
    AllowedBucketTypesForWithdrawing,
    freeDivision,
    playerActionOptions,
    sportTypeIds,
} from '../../../../../utils/eventConstants';
import Img from '../../../../../components/Img';
import { playerBucketShape, timeZoneShape, authShape, divisionShape } from '../../../../../propTypes';

const AcceptancePlayerBucket = (props) => {
    const {
        bucket,
        timeZone,
        addOptionsSpace,
        auth,
        gridLayout,
        currentDivision,
        hideUtrLabel,
        isCurrentMemberAdmin,
        selectedPlayers,
        isPttEvent,
        utrTypeId,
        sportTypeId,
        isOfficialHSEvent,
        showAgeColumn,
        showRatingNotLoggedIn,
        showRegDate,
        showRegStatus,
        onPlayerSelectClick,
        sendMessage,
        hasQualifyingBucket,
        hasAlternateBucket,
        allowOffPlatformPayments,
        isStripe,
        playerMenuOnSelected,
        isRegistrationClosed,
        isAcceptanceListLocked,
    } = props;

    const playerMenuOptions = useMemo(() => {
        if (!isCurrentMemberAdmin) {
            return [];
        }

        const playerActions = [];

        if (isAcceptanceListLocked && AllowedBucketTypesForMovingToMain.includes(bucket.type)) {
            playerActions.push({
                label: 'Move Player to Main',
                value: playerActionOptions.MAIN,
                acceptanceType: bucket.players[0]?.acceptanceType,
            });
        }

        if (isAcceptanceListLocked && hasQualifyingBucket && AllowedBucketTypesForMovingToQualifying.includes(bucket.type)) {
            playerActions.push({
                label: 'Move Player to Qualifying',
                value: playerActionOptions.QUALIFYING,
                acceptanceType: bucket.players[0]?.acceptanceType,
            });
        }

        if (isAcceptanceListLocked && hasAlternateBucket && AllowedBucketTypesForMovingToAlternate.includes(bucket.type)) {
            playerActions.push({
                label: 'Move Player to Alternate',
                value: playerActionOptions.ALTERNATE,
                acceptanceType: bucket.players[0]?.acceptanceType,
            });
        }

        if (isAcceptanceListLocked && AllowedBucketTypesForTagging.includes(bucket.type)) {
            playerActions.push({
                label: 'Tag player',
                value: playerActionOptions.TAG,
                acceptanceType: bucket.players[0]?.acceptanceType,
            });
        }

        const isFreeDivision = currentDivision?.price === freeDivision;
        const canShowRefundOption = !isStripe && !allowOffPlatformPayments && !isFreeDivision;
        if (AllowedBucketTypesForWithdrawing.includes(bucket.type)) {
            playerActions.push({
                label: isStripe ? 'Manage' : 'Withdraw',
                value: playerActionOptions.WITHDRAW,
                acceptanceType: bucket.players[0]?.acceptanceType,
            });

            if (canShowRefundOption) {
                playerActions.push({
                    label: 'Refund entry fees',
                    value: playerActionOptions.REFUND,
                    acceptanceType: bucket.players[0]?.acceptanceType,
                });
            }
        }

        if (bucket.type === acceptanceBucketTypeSortKey.WITHDRAWN) {
            if (isStripe) {
                playerActions.push({
                    label: 'Manage',
                    value: playerActionOptions.REFUND,
                    acceptanceType: bucket.players[0]?.acceptanceType,
                });
            } else if (!isFreeDivision && !allowOffPlatformPayments) {
                playerActions.push({
                    label: 'Refund entry fees',
                    value: playerActionOptions.REFUND,
                    acceptanceType: bucket.players[0]?.acceptanceType,
                });
            }
        }

        return playerActions;
    }, [bucket, isCurrentMemberAdmin, hasQualifyingBucket, hasAlternateBucket, currentDivision, isStripe, allowOffPlatformPayments, isRegistrationClosed, isAcceptanceListLocked]);

    const onPlayerMenuSelected = (player) => (selected, e) => {
        playerMenuOnSelected(e, player.playerId, selected);
    };

    const [isExpanded, setIsExpanded] = useState(true);

    const isTennisSportEvent = sportTypeId === sportTypeIds.TENNIS;
    const isPickleBallSportEvent = sportTypeId === sportTypeIds.PICKLEBALL;

    const isOwnPlayerCard = (player) => {
        return player.memberId === auth.user?.id;
    };

    const bucketName = acceptanceBucketTypeIds[bucket.type];

    return (
        <div className="mb24">
            <div
                className="utr-card player-card player-card-v3 clickable"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="list-view d-flex justify-content-between align-items-center">
                    <div className="name text-callout-small">
                        {bucketName}
                    </div>
                    <Img
                        className={`mr24 click-area-18 rotate ${isExpanded ? 'revert' : 'invert'}`}
                        src="/img/v4/icon-arrow-up.svg"
                    />
                </div>
            </div>
            {isExpanded && bucket.players.map((player) => {
                return (
                    <AcceptancePlayerCard
                        key={`bucket-${bucket.type}-player-${player.memberId}`}
                        addOptionsSpace={addOptionsSpace}
                        auth={auth}
                        currentDivisionId={currentDivision.id}
                        gridLayout={gridLayout}
                        hideUtrLabel={hideUtrLabel}
                        isCurrentMemberAdmin={isCurrentMemberAdmin}
                        isSelected={selectedPlayers.includes(player.memberId)}
                        menuOnSelected={onPlayerMenuSelected(player)}
                        menuOptions={playerMenuOptions}
                        onSelectClick={onPlayerSelectClick}
                        player={player}
                        sendMessage={sendMessage}
                        showCheckbox={isCurrentMemberAdmin}
                        showDecimals={isCurrentMemberAdmin || isOwnPlayerCard(player)}
                        showHighSchoolName={isOfficialHSEvent}
                        showCollegeName={isPttEvent}
                        showPickleBallRatings={isPickleBallSportEvent}
                        showPlayerPaymentStatus={isCurrentMemberAdmin}
                        showRatingNotLoggedIn={showRatingNotLoggedIn}
                        showAgeColumn={showAgeColumn}
                        showPosition={true}
                        showRegDate={showRegDate}
                        showRegStatus={showRegStatus}
                        showTennisRatings={isTennisSportEvent}
                        sportTypeId={sportTypeId}
                        utrTypeId={utrTypeId}
                        isPttEvent={isPttEvent}
                        timeZone={timeZone}
                    />
                );
            })}
        </div>
    );
};

AcceptancePlayerBucket.propTypes = {
    bucket: playerBucketShape.isRequired,
    timeZone: timeZoneShape.isRequired,
    addOptionsSpace: PropTypes.bool,
    auth: authShape.isRequired,
    gridLayout: PropTypes.arrayOf(PropTypes.string).isRequired,
    currentDivision: divisionShape.isRequired,
    hideUtrLabel: PropTypes.bool,
    isCurrentMemberAdmin: PropTypes.bool,
    selectedPlayers: PropTypes.arrayOf(PropTypes.number).isRequired,
    isPttEvent: PropTypes.bool,
    utrTypeId: PropTypes.number.isRequired,
    sportTypeId: PropTypes.number.isRequired,
    isOfficialHSEvent: PropTypes.bool,
    showAgeColumn: PropTypes.bool,
    showRatingNotLoggedIn: PropTypes.bool,
    showRegDate: PropTypes.bool,
    showRegStatus: PropTypes.bool,
    onPlayerSelectClick: PropTypes.func.isRequired,
    sendMessage: PropTypes.func.isRequired,
    hasQualifyingBucket: PropTypes.bool,
    hasAlternateBucket: PropTypes.bool,
    allowOffPlatformPayments: PropTypes.bool,
    isStripe: PropTypes.bool,
    playerMenuOnSelected: PropTypes.func.isRequired,
    isRegistrationClosed: PropTypes.bool,
    isAcceptanceListLocked: PropTypes.bool,
};

AcceptancePlayerBucket.defaultProps = {
    addOptionsSpace: false,
    hideUtrLabel: false,
    isCurrentMemberAdmin: false,
    isPttEvent: false,
    isOfficialHSEvent: false,
    showAgeColumn: false,
    showRatingNotLoggedIn: false,
    showRegDate: false,
    showRegStatus: false,
    hasQualifyingBucket: false,
    hasAlternateBucket: false,
    allowOffPlatformPayments: false,
    isStripe: false,
    isRegistrationClosed: false,
    isAcceptanceListLocked: false,
};

export default AcceptancePlayerBucket;
